import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useParams, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useWorkspace } from '../../lib/workspace';
import { useApi } from '../../lib/api';
import { getSocket } from '../../lib/socket';
import { subscribeChatUnreadChanged } from '../../lib/chatUnreadBus';
import { GreatLeapLogoIcon } from '../GreatLeapLogoIcon';
import { useNavGlider } from './useNavGlider';
import {
  House, Building2, Handshake,
  Newspaper, MessagesSquare, SquareCheckBig, TrendingUp, FolderKanban, FilePen, Presentation,
  FileChartColumn, IndianRupee, Target, Award, Layers,
  ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight,
} from 'lucide-react';

function playNudgeSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.18, 0.36].forEach(offset => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime + offset);
      osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + offset + 0.12);
      gain.gain.setValueAtTime(0.4, ctx.currentTime + offset);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + 0.14);
      osc.start(ctx.currentTime + offset);
      osc.stop(ctx.currentTime + offset + 0.15);
    });
    setTimeout(() => ctx.close(), 1000);
  } catch (_) {}
}

function useChatUnread(appId, myUserId) {
  const api = useApi();
  const { getToken } = useAuth();
  const [unread, setUnread] = useState(0);
  const apiRef = useRef(api);
  const appIdRef = useRef(appId);
  apiRef.current = api;
  appIdRef.current = appId;

  // message_saved and read_receipt can each trigger a refresh in quick
  // succession (e.g. sending a message fires message_saved immediately, then
  // this same client's own mark_read completes and broadcasts read_receipt
  // moments later) — two independent GET /chat requests in flight can
  // resolve out of order, so a slower earlier request's stale result could
  // overwrite a faster later one's correct count. Only apply a response if
  // it's from the most recently *issued* request.
  const requestSeq = useRef(0);
  const refresh = useRef(() => {
    const seq = ++requestSeq.current;
    apiRef.current.chatConversations(appIdRef.current)
      .then(convs => {
        if (seq !== requestSeq.current) return; // superseded by a newer refresh
        const total = (convs || []).reduce((s, c) => s + (c.unread_count || 0), 0);
        setUnread(total);
      })
      .catch(() => {});
  });

  useEffect(() => {
    if (!appId) return;
    refresh.current();
  }, [appId]);

  // Same-tab notification from Chat.jsx (e.g. a conversation was opened and
  // its unread_count cleared locally) — refreshes the badge immediately
  // instead of relying only on a socket event, which can be missed. See
  // chatUnreadBus.js.
  useEffect(() => {
    return subscribeChatUnreadChanged(() => refresh.current());
  }, []);

  useEffect(() => {
    if (!appId) return;
    let cancelled = false;
    // Regular messages only ever emit 'message_saved' now (Phase 1 removed
    // the pre-persistence 'new_message' broadcast for them) — this listener
    // previously listened for 'new_message', which meant the sidebar Chat
    // nav badge never refreshed on an incoming message and could drift out
    // of sync with the real unread count until a full page reload.
    const onNewMsg = () => { if (!cancelled) refresh.current(); };
    const onRead   = () => { if (!cancelled) refresh.current(); };
    const onNudge  = ({ conversation_id, sender_id }) => {
      if (cancelled) return;
      if (myUserId && String(sender_id) === String(myUserId)) return;
      try {
        const muted = JSON.parse(localStorage.getItem(`chat_mute_${conversation_id}`) || 'false');
        if (!muted) playNudgeSound();
      } catch { playNudgeSound(); }
    };

    getToken().then(token => {
      if (cancelled) return;
      const socket = getSocket(token, getToken);

      const doJoin = () => {
        if (socket._joinedAppId !== appId) {
          socket._joinedAppId = appId;
          socket.emit('join', { appId });
        }
        socket.on('message_saved', onNewMsg);
        socket.on('read_receipt', onRead);
        socket.on('nudge', onNudge);
      };

      if (socket.connected) {
        doJoin();
      } else {
        socket.once('connect', doJoin);
      }
    });

    return () => {
      cancelled = true;
      getToken().then(token => {
        const socket = getSocket(token);
        socket.off('message_saved', onNewMsg);
        socket.off('read_receipt', onRead);
        socket.off('nudge', onNudge);
      });
    };
  }, [appId, getToken]);

  return unread;
}

// Collapsed-rail logo: the segmented "O" ring from the GLOS wordmark.
// The viewBox is cropped to the ring's outer edge (r 34 + half the 14 stroke),
// so the ring fills `size` edge to edge.
function GlosMark({ size = 44 }) {
  return (
    <svg className="glos-mark" width={size} height={size} viewBox="9 9 82 82" overflow="visible" aria-hidden="true">
      <defs>
        <linearGradient id="glos-mark-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1739d6" />
          <stop offset="55%" stopColor="#0ab0e0" />
          <stop offset="100%" stopColor="#3fcf6a" />
        </linearGradient>
      </defs>
      {/* Four quarter arcs (one circle each so they can pop in one after another);
          the gaps sit on the axes like the wordmark's cuts. Animation: .glos-mark* in workspace.css. */}
      <g className="glos-mark-spin">
        {[0, 1, 2, 3].map(k => (
          <circle key={k} className="glos-mark-seg" style={{ '--d': `${0.2 + k * 0.12}s` }}
            cx="50" cy="50" r="34" fill="none" stroke="url(#glos-mark-grad)" strokeWidth="14"
            strokeDasharray="47.4 166.2" strokeDashoffset={-(3 + k * 53.4)} />
        ))}
      </g>
    </svg>
  );
}

const NAV_GROUPS = [
  {
    label: 'MAIN',
    items: [
      { to: 'home',                label: 'Home',             Icon: House,       },
      { to: 'my-organisation',     label: 'My Organisation',  Icon: Building2,                    },
      { to: 'clients',             label: 'Clients',          Icon: Handshake,                    },
    ],
  },
  {
    label: 'WORKSPACE',
    items: [
      { to: 'feed',             label: 'Feed',             Icon: Newspaper,      },
      { to: 'chat',             label: 'Chat',             Icon: MessagesSquare, },
      { to: 'tasks',            label: 'Tasks',            Icon: SquareCheckBig, },
      { to: 'performance',      label: 'Performance',      Icon: TrendingUp,     },
      { to: 'projects',         label: 'Projects',         Icon: FolderKanban,   projectsOnly: true },
      { to: 'contracts',        label: 'Agreements',       Icon: FilePen,        },
      { to: 'business-reviews', label: 'Business Reviews', Icon: Presentation,   hasTeamOnly: true },
    ],
  },
  {
    label: 'REPORTS & POLICIES',
    items: [
      {
        to: 'reports', label: 'Reports', Icon: FileChartColumn,
        dropdown: [
          { to: 'task-reports',        label: 'Task Report',         Icon: SquareCheckBig },
          { to: 'project-reports',     label: 'Project Report',      Icon: FolderKanban   },
          { to: 'financial-report',    label: 'Financial Report',    Icon: IndianRupee,    adminOnly: true },
          { to: 'contract-report',     label: 'Agreements Reports',  Icon: FilePen        },
          { to: 'performance-report',  label: 'Performance Report',  Icon: TrendingUp     },
          { to: 'goals-report',        label: 'Goals Report',        Icon: Target         },
          { to: 'appraisal-report',    label: 'Appraisal Report',    Icon: Award          },
          { to: 'other-reports',       label: 'Other Reports',       Icon: Layers         },
        ],
      },
    ],
  },
];

// Hover animation for each nav icon, keyed by label. Each name is a keyframe set
// in workspace.css (`.dock-item[data-anim="…"]:hover .sidebar-icon`), picked to
// suit the icon — the house hops, the chat bubble wiggles, the chart grows.
const ANIM_BY_LABEL = {
  Home: 'hop',
  'My Organisation': 'rise',
  Clients: 'shake',
  Feed: 'tilt',
  Chat: 'wiggle',
  Tasks: 'pop',
  Performance: 'grow',
  Projects: 'tilt',
  Agreements: 'tilt',
  'Business Reviews': 'swing',
  Reports: 'grow',
  'Financial Report': 'flip',
  'Goals Report': 'pop',
  'Appraisal Report': 'swing',
};
const navIconAnim = (label) => ANIM_BY_LABEL[label] || 'pop';

// The sidebar always starts collapsed. The choice is kept in memory only, so it
// survives moving between pages (and leaving/re-entering an app) but not a reload.
let collapsedMemory = true;

const REPORT_ROUTES = ['task-reports', 'project-reports', 'financial-report', 'contract-report', 'performance-report', 'goals-report', 'appraisal-report', 'other-reports'];

export default function Sidebar() {
  const { appId } = useParams();
  const location  = useLocation();
  const { myRole, me, app, organisation } = useWorkspace();
  const api       = useApi();
  const isCreator = myRole === 'super_admin';
  const isAdmin = myRole === 'admin' || myRole === 'super_admin';
  const chatUnread = useChatUnread(appId, me?.id);
  const [collapsed, setCollapsed] = useState(() => collapsedMemory);
  const navRef = useNavGlider();
  const [hasTeam, setHasTeam] = useState(false);
  const canSeeProjects = true;

  useEffect(() => {
    if (isAdmin) { setHasTeam(true); return; }
    api.hasTeam(appId)
      .then(res => setHasTeam(!!res.has_team))
      .catch(() => setHasTeam(false));
  }, [api, appId, isAdmin]);

  const isOnReport = REPORT_ROUTES.some(r => location.pathname.includes(r));
  const [reportsOpen, setReportsOpen] = useState(isOnReport);

  // Collapsed-mode hover label, drawn with fixed positioning outside the scrolling nav so it
  // is never clipped by it.
  const [hoverLabel, setHoverLabel] = useState(null);
  const showLabel = (e, text) => {
    if (!collapsed) return;
    const r = e.currentTarget.getBoundingClientRect();
    setHoverLabel({ text, top: r.top + r.height / 2, left: r.right + 12 });
  };
  const hideLabel = () => setHoverLabel(null);
  const labelHandlers = (text) => ({
    onMouseEnter: (e) => showLabel(e, text), onMouseLeave: hideLabel,
    onFocus: (e) => showLabel(e, text), onBlur: hideLabel,
  });

  const setCollapsedStored = (next) => {
    setHoverLabel(null);
    collapsedMemory = next;
    setCollapsed(next);
  };

  const companyName = organisation?.name || app?.name || '';
  const companyLogo = organisation?.logo_data || organisation?.logo_url || null;
  const companyInitials = (companyName || 'O').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => { setLogoFailed(false); }, [companyLogo]);
  const showLogo = companyLogo && !logoFailed;

  return (
    <aside className={`workspace-sidebar${collapsed ? ' sidebar-collapsed' : ''}`}>
      {/* Logo; expanded also pins the collapse toggle to the right edge */}
      <div className="sidebar-header">
        <Link to="/dashboard" className="sidebar-brand" aria-label="My Apps">
          {collapsed
            ? <span className="sidebar-mark"><GlosMark /></span>
            : <GreatLeapLogoIcon size={36} />}
        </Link>
        {!collapsed && (
          <button className="sidebar-toggle-btn" onClick={() => setCollapsedStored(true)}
            title="Collapse sidebar" aria-label="Collapse sidebar">
            <ChevronsLeft size={16} />
          </button>
        )}
      </div>
      {collapsed && (
        <button className="sidebar-toggle-btn" onClick={() => setCollapsedStored(false)}
          title="Expand sidebar" aria-label="Expand sidebar">
          <ChevronsRight size={16} />
        </button>
      )}

      {/* Nav */}
      <nav ref={navRef} className="sidebar-nav" onScroll={hideLabel}>
        <div className="nav-glider" aria-hidden="true" />
        {NAV_GROUPS.map((group) => {
          const visibleItems = group.items.filter((item) => {
            if (item.projectsOnly) return canSeeProjects;
            if (item.superAdminOnly) return isCreator;
            if (item.adminOnly) return isAdmin;
            if (item.memberOnly) return !isAdmin;
            if (item.hasTeamOnly) return hasTeam;
            return true;
          });

          if (visibleItems.length === 0) return null;

          return (
            <div key={group.label} className="sidebar-group">
              {!collapsed && <div className="sidebar-group-label">{group.label}</div>}
              {visibleItems.map(({ to, label, Icon, dropdown }) => {
                const badge = to === 'chat' && chatUnread > 0 ? chatUnread : 0;

                if (dropdown) {
                  const isParentActive = isOnReport;
                  return (
                    <div key={to}>
                      <button
                        id="tour-reports"
                        // Collapsed there's no room for the sub-menu, so open the panel with it shown.
                        onClick={() => {
                          if (collapsed) { setCollapsedStored(false); setReportsOpen(true); }
                          else setReportsOpen(o => !o);
                        }}
                        aria-label={label}
                        {...labelHandlers(label)}
                        className={`sidebar-link dock-item${isParentActive ? ' active' : ''}`}
                        data-anim={navIconAnim(label)}
                        style={collapsed ? undefined : { width: '100%' }}
                      >
                        <Icon size={21} strokeWidth={1.75} className="sidebar-icon" aria-hidden="true" />
                        {!collapsed && (
                          <>
                            <span style={{ flex: 1 }}>{label}</span>
                            {reportsOpen
                              ? <ChevronDown size={14} style={{ opacity: 0.6 }} />
                              : <ChevronRight size={14} style={{ opacity: 0.6 }} />
                            }
                          </>
                        )}
                      </button>

                      {!collapsed && reportsOpen && (
                        <div style={{ paddingLeft: 16, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {dropdown.filter(d => !d.adminOnly || isAdmin).map(({ to: subTo, label: subLabel, Icon: SubIcon }) => (
                            <NavLink
                              key={subTo}
                              to={`/apps/${appId}/${subTo}`}
                              className={({ isActive }) => `sidebar-link dock-item${isActive ? ' active' : ''}`}
                              data-anim={navIconAnim(subLabel)}
                              style={{ fontSize: 13.5, padding: '8px 12px' }}
                            >
                              <SubIcon size={15} className="sidebar-icon" aria-hidden="true" />
                              <span>{subLabel}</span>
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                const tourId =
                  to === 'feed'              ? 'tour-feed'         :
                  to === 'tasks'             ? 'tour-tasks'        :
                  to === 'chat'              ? 'tour-chat'         :
                  to === 'projects'          ? 'tour-projects'     :
                  to === 'setup/organisation'? 'tour-organisation' :
                  to === 'users'             ? 'tour-users'        :
                  to === 'setup/roles'       ? 'tour-roles'        :
                  to === 'clients'           ? 'tour-clients'      :
                  null;

                // Preserve the current query string (status/owner/etc. filters)
                // when re-clicking the section the user is already on — a plain
                // pathname `to` would otherwise strip those params and reset
                // pages like Tasks to their default filters.
                const targetPath = `/apps/${appId}/${to}`;
                const isSameSection = location.pathname === targetPath || location.pathname.startsWith(`${targetPath}/`);
                const linkTo = isSameSection ? { pathname: location.pathname, search: location.search } : targetPath;

                return (
                  <NavLink
                    key={to}
                    to={linkTo}
                    className={({ isActive }) => `sidebar-link dock-item${isActive ? ' active' : ''}`}
                    data-anim={navIconAnim(label)}
                    aria-label={label}
                    {...labelHandlers(label)}
                    {...(tourId ? { id: tourId } : {})}
                  >
                    <span className="sidebar-icon-wrap" style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                      <Icon size={21} strokeWidth={1.75} className="sidebar-icon" aria-hidden="true" />
                      {badge > 0 && (
                        <span style={{
                          position: 'absolute', top: -5, right: -7,
                          background: 'var(--accent)', color: '#fff',
                          borderRadius: 10, fontSize: 9, fontWeight: 700,
                          minWidth: 15, height: 15, lineHeight: '15px',
                          padding: '0 3px', textAlign: 'center',
                          boxSizing: 'border-box',
                        }}>
                          {badge > 99 ? '99+' : badge}
                        </span>
                      )}
                    </span>
                    {!collapsed && <span>{label}</span>}
                    {!collapsed && badge > 0 && (
                      <span style={{
                        marginLeft: 'auto', background: 'var(--accent)', color: '#fff',
                        borderRadius: 10, fontSize: 10, fontWeight: 700,
                        padding: '1px 7px',
                      }}>
                        {badge > 99 ? '99+' : badge}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      {collapsed && hoverLabel && (
        <div className="sidebar-tip" style={{ top: hoverLabel.top, left: hoverLabel.left }}>
          {hoverLabel.text}
        </div>
      )}

      {/* Company docked at the bottom (the user's own avatar is in the top bar) */}
      <div className="sidebar-company" {...labelHandlers(companyName)}>
        <span className={`sidebar-company-logo${showLogo ? ' has-logo' : ''}`}>
          {showLogo
            ? <img src={companyLogo} alt={companyName} onError={() => setLogoFailed(true)} />
            : companyInitials}
        </span>
        {!collapsed && <span className="sidebar-company-name">{companyName}</span>}
      </div>
    </aside>
  );
}
